import "./extensions/StringExtensions";
import "./extensions/SetExtensions";
import "./extensions/MapExtensions";
import "./extensions/ArrayExtensions";
import baseMerge from "lodash/_baseMerge.js";
import { monkeyPatch } from "./extensions/monkeyPatch";
import _ from "lodash";

import { Object3D, Quaternion, Vector2, Vector3 } from "three";

let id_counter = 0;

/**
 * Statische Utility-Sammlung zu den Themen Objekte und Collections
 */

/**
 * führt ein schnelles, stabiles sort mit frei definierbarer Comparator-Funktion wie bei array.sort aus aus,
 * allerdings nicht in-place, d.h. gibt eine sortierte Kopie zurück. Lodash Sort kann dagegen leider keine
 * Comparator-Funktion mit (a,b), sondern nur mit einem Property - facepalm
 */
export const stableSort = <T>(
  array: T[],
  comparator: (l: T, r: T) => number = (l: any, r: any) => l - r,
): T[] => {
  return array
    .map((element, index) => ({ element, index }))
    .sort((a, b) => comparator(a.element, b.element) || a.index - b.index)
    .map((entry) => entry.element);
};

/**
 * Sortiert einen azyklischen gerichteten Graphen, d.h. gibt eine Liste zurück,
 * in der Abhängigkeiten der Reihenfolge nach aufgelöst sind.
 *
 * Beispiel: wenn man ein Array von Aufgaben hat, und Aufgaben können voneinander abhängen,
 * dann kriegt man eine Reihenfolge zurück, in der die Aufgaben hinter einander abgearbeitet werden können.
 * Edges muss in diesem Beispiel zu einer Aufgabe A alle Aufgaben zurück geben,
 * die als Vorbedingung erledigt werden müssen (es reichen die direkten Vorgänger-Aufgaben).
 *
 * @see https://www.tutorialspoint.com/Topological-sorting-using-Javascript-DFS
 * @param {Array} array von beliebigen Element-Objekten
 * @param {function} edges ein Callback das zu jedem Element aus array die referenzierten Elemente zurückgibt
 * @returns {Array}
 */
export const topologicalSort = <T>(
  array: T[],
  edges: (element: T) => T[] = (element) => [],
): T[] => {
  let sorted: T[] = [];
  let visited = new Set<T>();
  const visit = (element: T) => {
    if (visited.has(element)) {
      return;
    }
    visited.add(element);
    edges(element).forEach(visit);
    sorted.push(element);
  };
  array.forEach(visit);
  return sorted;
};

/**
 * Findet alle Sequenzen von zusammenhängenden Teilgraphen eines Graphen
 *
 * @template N - Der Typ der Knoten im Graphen.
 * @param {Iterable<N>} nodes - Ein Array von Knoten im Graphen.
 * @param {(node: N, hops: number) => N[]} adjazenz - Eine Funktion, die einen Knoten nimmt und ein Array seiner adjazenten Knoten zurückgibt.
 * @returns {N[][]} - Eine Liste von Listen adjazenter Knoten, die die Eigenschaft erfüllen.
 */
export function findConnectedSubgraphs<N>(
  nodes: Iterable<N>,
  adjazenz: (node: N, hops?: number) => Iterable<N>,
): N[][] {
  const sequences: N[][] = [];
  // nodes is Iterable, need forEach or for..of
  for (const node of nodes) {
    const currentSequence: N[] = [];
    graphDFS(node, (n, hops) => {
      currentSequence.push(n);
      return adjazenz(n, hops);
    });
    if (currentSequence.length > 0) {
      sequences.push(currentSequence);
    }
  }
  return sequences;
}

/**
 * Führt eine Tiefensuche in einem Graphen aus. Kann verwendet werden als visitor oder finder.
 * @template N - Der Typ der Knoten im Graphen.
 * @template R - Das Result
 * @param N node - Der Startknoten
 * @param {(node: N, hops: number) => Iterable<N>} adjazenz - Eine Funktion, die einen Knoten nimmt und ein Array seiner adjazenten Knoten zurückgibt.
 * @param {(node: N, hops: number) => R} find - Eine Funktion, die für jeden Knoten aufgerufen wird. Falls die Funktion ein Ergebnis zurückgibt, bricht die Suche mit diesem Ergebnis ab.
 * @returns {R|undefined}
 * */

export function graphDFS<N, R>(
  node: N,
  adjazenz: (node: N, hops: number) => Iterable<N>,
  find: (node: N, hops: number) => R | undefined = (n) => undefined,
): R | undefined {
  const visited = new Set<N>();
  const stack: { node: N; hops: number }[] = [];
  stack.push({ node, hops: 1 });
  while (stack.length > 0) {
    const { node, hops } = stack.pop()!;
    const result = find(node, hops);
    if (result) {
      return result;
    }
    const children = adjazenz(node, hops);
    for (const child of children) {
      if (!visited.has(child)) {
        stack.push({ node: child, hops: hops + 1 });
        visited.add(child);
      }
    }
  }
  return undefined;
}

export { monkeyPatch };

/**
 * Erweitert ein Objekt um eine neue Property-Methode
 * @param {any} instanceToPatch
 * @param {string} propertyName
 * @param {{get,set}} getSet
 */
export const monkeyProperty = (
  instanceToPatch: any,
  propertyName: string,
  getset: PropertyDescriptor,
) => {
  if (typeof instanceToPatch[propertyName] === "undefined") {
    Object.defineProperty(instanceToPatch, propertyName, getset);
  }
};

/**
 * Ruft n mal valueOrFunction auf und gibt das Ergebnis als Array zurück.
 * valueOrFunction kann entweder eine Funktion(index,n) sein oder einfach ein Wert wie 'Hallo'.
 * @param {number} n
 * @param {any} valueOrFunction
 * @return {Array}
 */
export const repeatAsArray = <T>(
  n: number,
  valueOrFunction: T | ((index: number, n: number) => T),
): T[] => {
  let creator: (index: number, n: number) => T = _.isFunction(valueOrFunction)
    ? (valueOrFunction as any)
    : function () {
        return _.cloneDeep(valueOrFunction);
      };
  let array: T[] = [];
  for (let i = 0; i < n; i++) {
    array.push(creator(i, n));
  }
  return array;
};

/**
 * Setzt die Anzahl der Elemente eines Arrays auf den Parameter count, und löscht oder erzeugt dazu Elemente.
 * @param {Array} array
 * @param {number} count
 * @param {function} createElementCallback
 * @param {function} removeElementCallback
 * @return {Array}
 */
export const setArrayLength = <T>(
  array: T[],
  count: number,
  createElementCallback: (index: number) => T = () => undefined as unknown as T,
  removeElementCallback?: (item: T) => void,
): T[] => {
  while (count > array.length) {
    array.push(createElementCallback(array.length));
  }
  if (array.length > count) {
    if (removeElementCallback) {
      for (let i = count; i < array.length; i++) {
        removeElementCallback(array[i]);
      }
    }
    array.splice(count, array.length - count);
  }
  return array;
};

/**
 * Ähnlich wie array.map, aber verwendet vorhandene Objekte wieder.
 * @param {Array} sourceArray
 * @param {Array} destinationArray
 * @param {function} createElementCallback
 * @param {function} mapCallback
 * @returns {Array}
 * @example
 * Statt:
 * this.destinationArray = sourceArray.map(source=>new Destination().fromSource(source));
 * Schreibt man:
 * this.destinationArray = mapreuse(sourceArray,this.destinationArray,source=>new Destination(),(destination,source)=>destination.fromSource(source));
 * Dadurch wird das destinationArray und darin enthaltene Objekte wiederverwendet, und nur bei Bedarf Objekte neu erzeugt oder gelöscht.
 * mapCallback wird für jedes Objekt aufgerufen.
 */
export const mapreuse = <S, D>(
  sourceArray: S[] | null | undefined,
  destinationArray: D[] | null | undefined,
  createElementCallback: (source: S) => D,
  mapCallback: (dest: D, source: S) => D = (d, s) => d,
): D[] | null | undefined => {
  if (sourceArray == null) {
    return sourceArray as any;
  }
  if (destinationArray == null) {
    destinationArray = [];
  }
  const sourceArrayLength = sourceArray.length;
  while (sourceArrayLength > destinationArray.length) {
    destinationArray.push(createElementCallback(sourceArray[destinationArray.length]));
  }
  if (destinationArray.length > sourceArrayLength) {
    destinationArray.splice(sourceArrayLength, destinationArray.length - sourceArrayLength);
  }
  destinationArray.forEach((a, i) => {
    destinationArray![i] = mapCallback(a, sourceArray[i]);
  });
  return destinationArray;
};

/**
 * Entfernt Duplicate aus einem Array, benutzt intern als Vergleich IsEqualComparer(object,object)
 * @template T Der Typ der Array-Elemente.
 * @param {T[]} array Das Array, aus dem Duplikate entfernt werden sollen.
 * @param {boolean} [isSorted=false] Gibt an, ob das Array sortiert ist, was die Performance optimieren kann.
 * @return {T[]} Ein neues Array ohne Duplikate.
 **/
export const unique = <T>(array: T[], isSorted: boolean = false): T[] => {
  return isSorted ? _.sortedUniq(array) : _.uniq(array);
};

/**
 * Fügt einem Array alle Werte anderer Arrays hinzu (modifiziert das erste Array).
 * @param {Array} targetArray
 * @param {...Array} valuesToAddArrays
 * @returns {Array}
 */
export const addAll = <T>(
  targetArray: T[],
  ...valuesToAddArrays: (T[] | undefined | null)[]
): T[] => {
  if (!_.isArray(targetArray)) {
    throw new Error("targetArray must be an array");
  }
  valuesToAddArrays.forEach((valuesToAdd) => {
    if (valuesToAdd == null) {
      return;
    }
    if (!_.isArray(valuesToAdd)) {
      throw new Error("valuesToAdd must be an array");
    }
    Array.prototype.push.apply(targetArray, valuesToAdd);
  });
  return targetArray;
};

/**
 * prüft, ob zwei Arrays gleich sind (nicht deep)
 * @param array1
 * @param array2
 * @param {Function} morphism optionale element-Vergleichsfunktion
 * @returns {boolean|false|*}
 */
export const arraysEqual = <T>(
  array1: T[] | null | undefined,
  array2: T[] | null | undefined,
  morphism: (a: T, b: T) => boolean = (a, b) => a == b,
): boolean =>
  array1 == array2 ||
  (!!(array1 && array2) &&
    array1.length == array2.length &&
    array1.every((e, i) => morphism(e, array2[i])));

/**
 * Permutationsvergleich: Testet, ob zwei Arrays anhand einer Vergleichsfunktion (morphism) die gleichen Werte enhalten,
 * wobei die Reihenfolge nicht wichtig ist.
 * @param {function} morphism
 * @param {Array} array1
 * @param {Array} array2
 * @returns {boolean}
 */
export const areArraysIsomorph = <T>(
  morphism: (a: T, b: T) => boolean,
  array1: T[],
  array2: T[],
): boolean => {
  if (array1.length !== array2.length) {
    return false;
  }
  const length = array1.length;
  const visited = Array.from(array1).map((_) => false);
  outer: for (let i = 0; i < length; i++) {
    for (let j = 0; j < length; j++) {
      const targetIndex = (i + j) % length;
      if (!visited[targetIndex] && morphism(array1[i], array2[targetIndex])) {
        //wir haben Match gefunden
        visited[targetIndex] = true;
        continue outer;
      }
    }
    //innere Schleife durchgelaufen ohne Match
    return false;
  }
  //äußere Schleife durchlaufen, und immer einen Match gefunden
  return true;
};

/**
 * Zählt die Anzahl der Elemente, auf welche die predicate - Funktion zutrifft.
 * @param {Array} array
 * @param {function} predicate
 * @returns {number}
 */
export const count = <T>(
  array: T[] | null | undefined,
  predicate: (item: T) => boolean,
): number => {
  if (array == null) {
    return 0;
  }
  let c = 0;
  for (let i = 0; i < array.length; i++) {
    if (predicate(array[i])) {
      c++;
    }
  }
  return c;
};

/**
 * gibt den Index des ersten Min-Elementes eines Arrays zurück, bezogen auf eine Abbildung auf die reelen Zahlen zurück, oder -1, falls das Array leer ist.
 * @param {Array} array
 * @param {function} orderNumberFunction
 * @returns {number}
 */
export const minIndexBy = <T>(
  array: T[],
  orderNumberFunction: (x: T) => number = (x: any) => x,
): number => {
  return array.indexOf(_.minBy(array, orderNumberFunction) as T);
};

/**
 * gibt den Index des ersten Max-Elementes eines Arrays zurück, bezogen auf eine Abbildung auf die reelen Zahlen zurück, oder -1, falls das Array leer ist.
 * @param {Array} array
 * @param {function} orderNumberFunction * @return {*}
 * @returns {number}
 */
export const maxIndexBy = <T>(
  array: T[],
  orderNumberFunction: (x: T) => number = (x: any) => x,
): number => {
  return array.indexOf(_.maxBy(array, orderNumberFunction) as T);
};

/**
 * Summiert alle Werte eines Array. Leere Arrays ergeben null.
 * @param {Array} array
 * @returns {number}
 */
export const sum = (array: number[] = []): number => _.reduce(array, (acc, n) => acc + (n ?? 0), 0);

/**
 * Multipliziert alle Werte eines Arrays. Leere Arrays ergeben 1.
 * @param {Array} array
 * @returns {number}
 */
export const product = (array: number[] = []): number =>
  _.reduce(array, (acc, n) => acc * (n ?? 1), 1);

/**
 * Verzögert den Aufruf einer Funktion, bis predicateWaitingForToBecomeTrue true wird oder bis maxWaitTime
 * erreicht ist, falls > 0
 * @param {function} functionDelayed
 * @param {function} predicateWaitingForToBecomeTrue
 * @param {number} maxWaitTime
 * @param {number} pollIntervall
 * @param {function} onTimeout
 */
export const delayUntil = (
  functionDelayed: () => void,
  predicateWaitingForToBecomeTrue: () => boolean,
  maxWaitTime: number = 0,
  pollIntervall: number = 100,
  onTimeout: () => void = () => {},
): void => {
  let start = Date.now();

  function waitOnce() {
    if (predicateWaitingForToBecomeTrue()) {
      functionDelayed();
    } else if (maxWaitTime == 0 || Date.now() < start + maxWaitTime) {
      _.delay(waitOnce, pollIntervall);
    } else {
      onTimeout();
    }
  }

  waitOnce();
};

/**
 * Ruft einen Constructor mit einem Argumentarray auf.
 * @see http://nullprogram.com/blog/2013/03/24/
 * @param {function} constructorFunction
 * @param {Array} argumentArray
 * @returns {any}
 */
export const invokeConstructor = (constructorFunction: any, argumentArray: any[]): any => {
  let constructorArguments = [constructorFunction];
  addAll(constructorArguments, argumentArray);
  let factory = constructorFunction.bind.apply(constructorFunction, constructorArguments);
  return new factory();
};

/**
 * Ruft ein Callback für jeden Eintrag eines Arrays (value,index) oder eines Objektes (value,key) auf.
 * @param {any} Obejct
 * @param {function} callback
 */
export const forArrayOrObject = (
  object: any,
  callback: (value: any, key: any, length: number) => void,
): void => {
  if (_.isArray(object)) {
    object.forEach((value, index) => callback(value, index, object.length));
  } else {
    let keys = _.sortBy(_.keys(object) as string[]);
    keys.forEach((key) => callback(object[key], key, keys.length));
  }
};

/**
 * Prüft im Gegensatz zu lodash / ramda auch auf Leerstrings, null, undefined, leere Arrays und Objekte ohne
 * Properties
 * @param {any} value
 * @returns {boolean}
 */
export const isEmpty = (value: any): boolean => {
  return (
    value === null ||
    typeof value === "undefined" ||
    value === "" ||
    value.length === 0 ||
    value.size === 0 ||
    (!Array.isArray(value) && typeof value === "object" && Object.keys(value).length === 0)
  );
};

/**
 * prüft zwei beliebige Objekte auf Gleichheit.
 * Dabei werden enthaltene Floats, Vectoren, Quaternionen, Box3 und weitere auf Minimaldistanz hin überprüft
 * statt auf exakter Gleichheit wegen eventueller Floating-Point-Differenzen.
 * Speziell für Vectoren, Quaternionen, Matrix wird mit der dortigen isEqualEpsilon verglichen,
 * weil nur so im Rahmen eines allgemeinen Vergleichs sicher zu stellen ist, dass Callback-Member-Variablen wie
 * onChangeCallback Teil des Vergleichs sind
 *
 * @param {any} a Vergleichsobjekt 1
 * @param {any} b Vergleichsobjekt 2
 * @param {number} epsilon Erlaubte Floatingpointvarianz
 * @param {boolean} allowIsomophArrays Beachte die reihenfolge bei Arrays nicht
 * @returns {boolean}
 */
export const isEqualEpsilon = (
  a: any,
  b: any,
  epsilon: number = 0.000001,
  allowIsomophArrays: boolean = false,
): boolean => {
  return _.isEqualWith(a, b, (a: any, b: any) => {
    if (
      a != null &&
      (a as any).isEqualEpsilon !== undefined &&
      (a as any).isEqualEpsilon(b, epsilon)
    ) {
      return true;
    }
    if (a == b) {
      return true;
    }
    if (_.isNumber(a) && _.isNumber(b)) {
      return Math.abs(a - b) < epsilon;
    }
    if (allowIsomophArrays && Array.isArray(a) && Array.isArray(b)) {
      return areArraysIsomorph((x, y) => isEqualEpsilon(x, y, epsilon, allowIsomophArrays), a, b);
    }
    return undefined;
  });
};

/**
 * wrapp func in eine neue Funktion, welche die Ein-/Ausgabe loggt.
 * @param {string} funcName
 * @param {function} func
 * @returns {any}
 */
export const logCall = <F extends (...args: any[]) => any>(funcName: string, func: F): F => {
  return ((...args: any[]) => {
    console.log("> " + funcName, args);
    const result = func(...args);
    console.log("< " + funcName + " result = ", result);
    return result;
  }) as F;
};

export type CacheFactoryOptions<F extends (...args: any[]) => any> = {
  valueFactory: F;
  argsCaller?: (callback: any) => (...args: any[]) => any;
  keyMapper?: (...args: any[]) => any;
  mapConstructor?: () => Map<any, any> | WeakMap<any, any>;
};

/**
 * Erzeugt einen cache (memoized eine pure function).
 * Mittels resetCaches() wird jeder hierüber erzeugte Cache invalidiert.
 * @param {function} valueFactory bei einem Cachemiss wird diese Funktion aufgerufen, um die Rückgabe zu erzeugen.
 * @param {function} argsCaller ruft callback mit den Argumenten auf
 * @param {function} keyMapper mappt die Eingabeparameter auf den Cachekey
 * @param {function} mapConstructor erzeugt die Map
 * @returns {function}
 */
export const cacheFactory = <F extends (...args: any[]) => any>({
  valueFactory,
  argsCaller = (callback: any) =>
    (...args: any[]) =>
      callback(...args),
  keyMapper = (...args: any[]) => args,
  mapConstructor = () => new Map<any, any>(),
}: CacheFactoryOptions<F>): F => {
  const cache = (
    mapGetter: () => Map<any, any> | WeakMap<any, any>,
    keyMapper: any,
    valueFactory: any,
  ): any =>
    argsCaller((...args: any[]) => {
      const key = (keyMapper as Function)(...args); // fix typing for mappers
      const map = mapGetter();
      if (map.has(key)) {
        return map.get(key);
      }
      const value = valueFactory(...args);
      map.set(key, value);
      return value;
    });

  const mapGetter = (function getMapImpure() {
    let map: Map<any, any> | WeakMap<any, any>, cacheID: number;
    return () => {
      if (cacheID != CACHE_ID) {
        cacheID = CACHE_ID;
        map = mapConstructor();
      }
      return map;
    };
  })();
  return valueFactory
    ? cache(mapGetter, keyMapper, valueFactory)
    : (((key: any, valueFactory: any) => cache(mapGetter, keyMapper, valueFactory)(key)) as any);
};

/**
 * Siehe auch MultiMap.memoize für eine evtl. performantere alternative mittels MultiMap.
 *
 * Kapselt eine Funktion so, dass sie gecached wird, d.h. bei gleichen Aufrufparametern
 * wird die Funktion nur einmal aufgerufen, ansonsten der Wert des Caches zurückgegeben.
 * Teuer: Die Aufrufparameter werden in einen String umgewandelt mittels JSON.stringify.
 * Hinweis: cacheIdentity hat eine bessere Performance.
 * Mittels resetCaches() wird jeder hierüber erzeugte Cache invalidiert.
 * @example
 * const add = cache((a)=>{
 *    console.log('aufruf '+a);
 *    return a+1
 * });
 * add(1);
 * // ergibt 2 und logausgabe 'aufruf 1'
 * add(1);
 * // ergibt 2 und keine logausgabe
 * @param {function} valueFactory bei einem Cachemiss wird diese Funktion aufgerufen, um die Rückgabe zu erzeugen.
 * @returns {function}
 */
export const cache = (valueFactory: (...args: any[]) => any) =>
  cacheFactory({
    valueFactory,
    keyMapper: (...args) => cacheKey(...args),
  });

/**
 * Wie cache, allerdings wird nur ein Aufrufparamter unterstützt, der auf Identität verglichen wird.
 * Wenn die Option nullToNull=true ist, werden null / undefined direkt zurückgegeben.
 * Mittels resetCaches() wird jeder hierüber erzeugte Cache invalidiert.
 * @example
 * Es gibt zwei mögliche Wege, einen Cache zu benutzen:
 * 1. Die Valuefactory wird bei der Initialisierung angegeben (Normalfall):
 * myCache = cacheIdentity((mmr)=>this.loadModelPromise(mmr.Model,mmr.MaterialMap));
 * const promise = myCache(mmr);
 * 2. Die Valuefactory wird bei der Initialisierung weggelassen und bei der Verwendung angegeben:
 * myCache = cacheIdentity();
 * const promise = myCache(mmr,(mmr)=>this.loadModelPromise(mmr.Model,mmr.MaterialMap));
 * @param {function} valueFactory bei einem Cachemiss wird diese Funktion aufgerufen, um die Rückgabe zu erzeugen.
 * @param {any} options
 * @returns {function}
 */
export const cacheIdentity = (
  valueFactory?: (...args: any[]) => any,
  options: { nullToNull?: boolean } & any = { nullToNull: false },
) =>
  cacheFactory({
    valueFactory,
    argsCaller:
      (callback) =>
      (...args: any[]) => {
        if (args.length > 1) {
          throw new Error("cache supports just a single parameter");
        }
        const a = args[0];
        if (options.nullToNull && a == null) {
          return a;
        }
        return callback(a);
      },
    keyMapper: (x) => x,
    ...options,
  });

/**
 * Wie cacheIdentity, allerdings mit einer WeakMap, d.h., nicht verwendete Objekte werden automatisch Garbage Collected.
 * WeakMaps können nur auf Objekte angewendet werden, nicht auf primitive Datentypen wie null, undefined, bool, string, number.
 * Mittels resetCaches() wird jeder hierüber erzeugte Cache invalidiert.
 * @param {function} valueFactory bei einem Cachemiss wird diese Funktion aufgerufen, um die Rückgabe zu erzeugen.
 * @param {any} options
 * @returns {function}
 */
export const cacheWeak = (valueFactory?: (...args: any[]) => any, options = {}) =>
  cacheIdentity(valueFactory, {
    nullToNull: true,
    mapConstructor: () => new WeakMap(),
  });

/**
 * Ein sehr einfacher Cache für nur einen Wert / memoize
 * @param {function} singleValueFactory
 * @returns {function(): *}
 */
export const cacheSingle = <T>(singleValueFactory: () => T): (() => T) => {
  let value: T | undefined = undefined;
  return () => {
    if (value == undefined) {
      value = singleValueFactory();
    }
    return value;
  };
};

let CACHE_ID = 0;

/**
 * hiermit werden alle mittels cache erzeugten Caches invalidiert
 */
export const resetCaches = () => {
  CACHE_ID++;
  console.log("resetCaches()");
};

const _cacheKeyIntern = (arg: any): string | null | any => {
  if (arg == null) {
    return null;
  }
  const type = typeof arg;
  if (type === "object") {
    // FIXME Reihenfolge der Serialisierung ist evtl. nicht eindeutig :-((
    return JSON.stringify(arg);
    // return JSON.stringify(arg, Object.keys(arg).sort());
  }
  if (type === "function") {
    throw new Error("a function element is not supported as part of a cacheKey");
  }
  return arg;
};

/**
 * Wandelt ein Array von Objekten oder Primitiven in einen eindeutigen primitiven Wert um, meist ein String.
 * @param {...any} args
 * @returns {String}
 */
export const cacheKey = (...args: any[]): any => {
  if (args.length == 0) {
    return null;
  }
  if (args.length == 1 && typeof args[0] !== "object") {
    return args[0];
  }
  return _cacheKeyIntern(args);
};

/**
 * ruft eine Funktion solange auf, bis sie false zurückliefert
 * @param {function} callback
 */
export const iterateWhileDirty = (callback: () => boolean): void => {
  let dirty;
  do {
    dirty = callback();
  } while (dirty);
};

/**
 * Iteriert über alle Properties eines verschachtelten Objektes und klopft es auf
 * eine Ebene flach, wobei Keys mit Punkt concatiniert werden.
 * @param {{}} obj
 * @returns {{}}
 * @example
 * flattenObject({
 *  'a': 1,
 *  b: { 'c': 2, 'd': 3  }
 * });
 * // ergibt
 * { 'a':1, 'b.c': 2, 'b.d': 3 }
 */
export const flattenObject = (obj: any): any => {
  if (obj instanceof Vector3) {
    return { x: obj.x, y: obj.y, z: obj.z };
  }
  if (obj instanceof Vector2) {
    return { x: obj.x, y: obj.y };
  }
  if (obj instanceof Quaternion) {
    return { x: obj.x, y: obj.y, z: obj.z, w: obj.w };
  }
  let toReturn: any = {};
  for (let i in obj) {
    if (!obj.hasOwnProperty(i)) {
      continue;
    }
    if (typeof obj[i] == "object") {
      let flatObject = flattenObject(obj[i]); // Fix this.flattenObject -> flattenObject
      for (let x in flatObject) {
        if (!flatObject.hasOwnProperty(x)) {
          continue;
        }
        toReturn[i + "." + x] = flatObject[x];
      }
    } else {
      toReturn[i] = obj[i];
    }
  }
  return toReturn;
};

/**
 * Umkehrfunktion zu flattenObjekt
 * @param {{}} obj
 * @returns {{}}
 * @example
 * unflattenObject({ 'a':1, 'b.c': 2, 'b.d': 3 });
 * // ergibt
 * {
 *  'a': 1,
 *  b: { 'c': 2, 'd': 3  }
 * }
 */
export const unflattenObject = (obj: any): any => {
  let toReturn = {};
  for (let i in obj) {
    if (!obj.hasOwnProperty(i)) {
      continue;
    }
    _.set(toReturn, i, obj[i]);
  }
  return toReturn;
};

/**
 * <strong>Statt lodash.defaultsDeep sollte stets defaultsDeepSafe verwendet werden.</strong>
 * Grund: lodash.defaultsDeep({object: ein3dObject},{object: anderes3dObject}) setzt ein3dObject.children =
 * anderes3dObject.children falls ein3dObject.children leer ist, was den Szene-Graph komplett zerhaut (zwei Objekte
 * haben dann dasselbe Child, welches aber nur ein Parent hat). Eigentlich sollte sowas ja klar sein, aber falls
 * man so blöde ist wie ich und sich deswegen stundenlang einen Wolf sucht, wird lodash.defaultsDeep
 * gefährlich. defaultsDeepSafe ist nicht neu programmiert sondern erweitert lodash.defaultsDeep so, dass nicht mehr zwei
 * Nicht-Konfigurationsobjekte gemergt werden. Konfigurationsobjekte sind solche mit anonymer Klasse (Plain
 * Objects) wie {a:'Hallo',b:'Welt'}. Bei allen anderen Objekten - wie THREE.Object3D - wird nur die Referenz
 * ausgetauscht. Die Methode entspricht so eher der intuitiven Erwartung.
 * @example
 * // Konkrete Fälle, wodefaultsDeepSafe von lodash.defaultsDeep abweicht:
 * defaultsDeepSafe({ a: Vector(1,1,1) }, { a: Vector(2,2,2) } ) => { a: Vector(1,1,1) }​ // hier wird nichts gemergt, nur die Referenz ausgetauscht
 * defaultsDeepSafe({ a: Vector(1,1,1) }, { a: {x:2} } ) => { a: Vector(1,1,1) } // hier wird nichts gemergt, nur die Referenz ausgetauscht
 * // Folgendes Verhalten ist dagegen in beiden Implementierungen gleich:
 * defaultsDeepSafe({ a: {x:1} }, { a: Vector(2,2,2) } ) => { a: Vector(1,2,2) } // hier wird gemergt, weil das erste ein Konfigurationsobjekt ist
 * // In allen anderen Fällen ist das Ergebnis gleich.
 * @param {...Object} args
 * @returns {Object}
 */
export const defaultsDeepSafe = (...args: any[]): any => {
  args.push(undefined, _safeDefaultsDeepCustomizer);
  return _.mergeWith.apply(undefined, args as any);
};

/**
 * wie _.merge, aber Safe (defaultsDeepSafe)
 * @param {...Object} args
 * @returns {Object}
 */
export const mergeSafe = (...args: any[]): any => {
  // @ts-ignore
  return _.mergeWith(...args, _safeDefaultsDeepCustomizer);
};

const _safeDefaultsDeepCustomizer = (
  objValue: any,
  srcValue: any,
  key: any,
  object: any,
  source: any,
  stack: any,
): any => {
  if (_.isObject(objValue) && _.isObject(srcValue)) {
    if (!_.isPlainObject(objValue) && !_.isPlainObject(srcValue)) {
      if (_.isFunction((objValue as any).clone)) {
        return (objValue as any).clone();
      }
      return objValue;
    }
    baseMerge(
      objValue,
      srcValue,
      undefined,
      _safeDefaultsDeepCustomizer,
      stack.set(srcValue, objValue),
    );
  }
  return objValue;
};

/**
 * Diese Methode ist wie lodash.cloneDeep, aber alle Objekte, welche eine clone - Methode haben, werden
 * darüber gecloned, was auf fast alle ThreeJS-Objekte zutrifft.
 * @param {Object} value
 * @returns {Object}
 */
export const cloneDeep = <T>(value: T): T => {
  return _.cloneDeepWith(value, (v) => {
    if (v != null && _.isFunction((v as any).clone)) {
      return (v as any).clone();
    }
  });
};

/**
 * Gibt für ein Source-Verzeichnis eine Map Klassenname zu Klasse zurück.
 * @example
 * slurpDirectory(require.context('./modelTypes/',true));
 * @param {require.context} requireContext
 * @returns Object
 */
export const slurpDirectory = (
  requireContext: __WebpackModuleApi.RequireContext,
): Record<string, any> => {
  let result: Record<string, any> = {};
  requireContext.keys().forEach((filepath) => {
    const module = requireContext(filepath);
    const name = filepath.substringAfterLast("/").substringBefore(".");
    if (result[name] === undefined) {
      result[name] = module[name] ?? module.default ?? module;
    }
  });
  return result;
};

/**
 * Erzeugt eine interne eindeutige ID als etwa 12-stelligen String zur Basis 36. Diese ID setzt sich aus der aktuellen Millisekunde (modulo 20 Jahre)
 * und einer 6-stelligen Zufallszahl zusammen und kann als global eindeutig im Rahmen einer Konfigurator-App über alle Benutzer angesehen werden.
 * @example
 * createUniqueIdString() => "yi$cX5xeX$M2"
 * @returns {string}
 */
export const createUniqueIdString = (): string =>
  Math.floor(Math.random() * 1000000).toString(36) +
  (Date.now() % (20 * 365 * 24 * 60 * 60 * 1000)).toString(36);

/**
 * Erzeugt eine interne eindeutige ID als Zahl, die nicht zur Serialisierung taugt, aber im Rahmen
 * einer Browsersitzung der App eindeutig ist.
 * @example
 * createId() => 0
 * @returns {number}
 */
export const createId = (): number => {
  return id_counter++;
};

/**
 * setzt die id zurück auf 0. Vorsicht!! Falls man createId() für Arrangement-Objekte benutzt, sollte man
 * danach auch APP.representer.reset() aufrufen.
 */
export const resetId = (): void => {
  id_counter = 0;
};

/**
 * gibt den Typ bzw. Klassennamen eines Objektes zurück
 * @param {any} object
 * @returns {string}
 */
export const typeName = (object: any): string => {
  if (object == null) {
    return "null";
  }
  if (object.constructor?.CLASS_NAME) {
    return object.constructor?.CLASS_NAME;
  }
  if (object instanceof Object3D && !isEmpty(object.name)) {
    return object.name;
  }
  if ((object as any).type && object.hasOwnProperty("type")) {
    return (object as any).type;
  }

  if (!isEmpty(object.constructor?.name)) {
    return object.constructor?.name;
  }
  if ((object as any).type) {
    return (object as any).type;
  }
  let funcNameRegex = /function (.{1,})\(/;
  let results = funcNameRegex.exec(object.constructor.toString());
  if (results && results.length > 1 && results[1] != "onRotationChange") {
    return results[1];
  }
  return typeof object;
};

/**
 * Wie _.partition, aber mit index als 2. Parameter, wie es halt sinn macht... (kopfschüttel): https://github.com/lodash/lodash/issues/2641
 * @param {Array} list
 * @param {function} predicate
 * @returns {Array}
 */
export const partition = <T>(
  list: T[],
  predicate: (element: T, index: number) => boolean,
): [T[], T[]] => {
  const a: T[] = [];
  const b: T[] = [];
  list.forEach((element, index) => (predicate(element, index) ? a : b).push(element));
  return [a, b];
};

/**
 * Wie _.groupBy, aber mit index als 2. Parameter, und mit Map als ergebnis, wie es halt sinn macht... (kopfschüttel): https://github.com/lodash/lodash/issues/2641
 * @param {Array} list
 * @param {function} segmentFn
 * @returns {Map}
 */
export const groupBy = <T, K>(
  list: T[],
  segmentFn: (element: T, index: number) => K,
): Map<K, T[]> => {
  const map = new Map<K, T[]>();
  list.forEach((element, index) => {
    const segment = segmentFn(element, index);
    let l = map.get(segment);
    if (l == null) {
      l = [];
      map.set(segment, l);
    }
    l.push(element);
  });
  return map;
};

/**
 * Wie Commons.groupBy, aber segmentFn gibt ein Array von Keys zurück, d.h. jeder Entry kann mehreren Segmenten zugeordnet sein
 * @param {any[]} list
 * @param {function} segmentFn
 * @returns {Map}
 */
export const multiGroupBy = <T, K>(
  list: T[],
  segmentFn: (element: T, index: number) => K[],
): Map<K, T[]> => {
  const map = new Map<K, T[]>();
  list.forEach((element, index) => {
    const segmentList = segmentFn(element, index);
    segmentList.forEach((segment) => {
      let l = map.get(segment);
      if (l == null) {
        l = [];
        map.set(segment, l);
      }
      l.push(element);
    });
  });
  return map;
};

/**
 * Wie groupBy, aber kann sowohl keys als auch values transformieren, und fügt
 * Werte unique und flat ein
 * @param {Array<any>} list
 * @param {function} key
 * @param {function} values
 * @return {Map}
 */
export const groupByFlat = <T, K, V>(
  list: T[],
  key: (x: T, index: number) => K = (x, index) => x as any,
  values: (x: T, index: number) => V = (x, index) => x as any,
): Map<K, V[]> => {
  const map = new Map<K, any>();
  list.forEach((x, index) => {
    const k = key(x, index);
    const v = values(x, index);
    let set = map.get(k);
    if (set == null) {
      set = new Set();
      map.set(k, set);
    }
    if (Array.isArray(v)) {
      v.forEach((x) => set.add(v));
    } else {
      set.add(v);
    }
  });
  map.forEach((valuesSet, key) => {
    // values is a Set.
    const a = Array.from(valuesSet);
    map.set(key, a);
  });
  return map as Map<K, V[]>;
};

/**
 * Gruppiert die Elemente eines Arrays in ein Array von Segment-Arrays gleichen Wertes von segmentFn.
 * @param {any[]} list das zu segmentierende Array
 * @param {function} segmentFn optional mappt das Element auf einen Segment-Key. Default ist die Identität
 * @returns {any[]}
 * @example
 * segments([1,1,1,2,2,2,1,1,1,1,3,3,5]) => [[1,1,1],[2,2,2],[1,1,1,1],[3,3],[5]]
 */
export const segments = <T>(
  list: T[],
  segmentFn: (x: T, index: number) => any = (x) => x,
): T[][] => {
  const segments: T[][] = [];
  if (list && list.length > 0) {
    let current: T[] | null = null;
    let lastKey = undefined;
    list.forEach((element, index) => {
      const currentKey = segmentFn(element, index);
      if (lastKey !== currentKey || current == null) {
        current = [];
        segments.push(current);
      }
      current.push(element);
      lastKey = currentKey;
    });
  }
  return segments;
};

/**
 * Iteriert über eine Liste und übergibt an den Iterator die Info, ob es das erste oder letzte Elemente ist
 * @param {any[]} list
 * @param {function} iterator
 */
export const forEachFirstLast = <T>(
  list: T[],
  iterator: (element: T, index: number, isFirst: boolean, isLast: boolean) => void = (
    element,
    index,
    isFirst,
    isLast,
  ) => {},
): void => {
  const last = list.length - 1;
  list.forEach((element, index) => {
    const isFirst = index == 0;
    const isLast = index == last;
    iterator(element, index, isFirst, isLast);
  });
};

/**
 * Mapt eine Liste und übergibt an die Map-Funktion die Info, ob es das erste oder letzte Elemente ist
 * @param {any[]} list
 * @param {function} mapper
 * @returns {any[]}
 */
export const mapFirstLast = <T, U>(
  list: T[],
  mapper: (element: T, index: number, isFirst: boolean, isLast: boolean) => U = (
    element,
    index,
    isFirst,
    isLast,
  ) => ({}) as U,
): U[] => {
  const last = list.length - 1;
  return list.map((element, index) => {
    const isFirst = index == 0;
    const isLast = index == last;
    return mapper(element, index, isFirst, isLast);
  });
};

/**
 * Wie _.intersectionBy, allerdings wird als erster Parameter ein Array von Array erwartet, statt einer Parameterliste von Arrays.
 * @param {any[]} array
 * @param {function} segmentFn
 * @returns {any[]}
 */
export const intersectionBy = (array: any[], segmentFn: any): any[] => {
  const args = _.clone(array);
  args.push(segmentFn);
  return _.intersectionBy.apply(undefined, args);
};

/**
 * geht alle Paar-Kombinationen eines Arrays mit sich selbst durch.
 * Beispiel: [a,b,c] liefert die Aufrufkette (a,b),(a,c),(b,c)
 * @param {any[]} array
 * @param {function} callback mit signatur (element1,element2,index1,index2)
 */
export const pairCombinations = <T>(
  array: T[],
  callback: (e1: T, e2: T, i1: number, i2: number) => void,
): void => {
  const l = array.length;
  for (let i = 0; i < l; i++) {
    for (let j = i + 1; j < l; j++) {
      callback(array[i], array[j], i, j);
    }
  }
};

/**
 * Wie JSON.stringify, kommt aber mit rekursiven Datenstrukturen zurecht. Praktisch für Logausgaben.
 * @param {any} obj
 * @param {function} replacer
 * @param {number} indent
 * @returns {string}
 */
export const stringifyOnce = (
  obj: any,
  replacer?: (key: string, value: any) => any,
  indent?: number,
): string => {
  let printedObjects: any[] = [];
  let printedObjectKeys: string[] = [];

  function printOnceReplacer(key: string, value: any) {
    if (printedObjects.length > 2000) {
      // browsers will not print more than 20K, I don't see the point to allow 2K.. algorithm will not be fast anyway if we have too many objects
      return "object too long";
    }
    let printedObjIndex: any = false;
    printedObjects.forEach(function (obj, index) {
      if (obj === value) {
        printedObjIndex = index;
      }
    });

    if (key == "") {
      //root element
      printedObjects.push(obj);
      printedObjectKeys.push("root");
      return value;
    } else if (printedObjIndex + "" != "false" && typeof value == "object") {
      if (printedObjectKeys[printedObjIndex] == "root") {
        return "(pointer to root)";
      } else {
        return (
          "(see " +
          (!!value && !!value.constructor ? value.constructor.name.toLowerCase() : typeof value) +
          " with key " +
          printedObjectKeys[printedObjIndex] +
          ")"
        );
      }
    } else {
      let qualifiedKey = key || "(empty key)";
      printedObjects.push(value);
      printedObjectKeys.push(qualifiedKey);
      if (replacer) {
        return replacer(key, value);
      } else {
        return value;
      }
    }
  }

  return JSON.stringify(obj, printOnceReplacer, indent);
};

/**
 * Pendant zur serverseitigen PHP-Funktion Helper::safeFileName. Somit können z.B. normalisierte Dateinamen abgefragt werden.
 * @param {string} name
 * @returns {string}
 *
 */
export const safeFileName = (name: string): string | null => {
  return name ? name.replace(/[\/\\:~&#=\'`\+]+/gi, "").replace(/\s\s/g, " ") : null;
};

/**
 * Wie Object.getOwnPropertyNames, aber auch für vererbte Properties der Super-Klassen.
 * @param {Object} obj
 * @returns {Array<string>}
 */
export const getAllPropertyNames = (obj: any): string[] => {
  let props: string[] = [];
  do {
    props = props.concat(Object.getOwnPropertyNames(obj));
  } while ((obj = Object.getPrototypeOf(obj)));
  return props;
};

/**
 * Gibt alle Methoden eines Objektes zurück, die mit einem bestimmten Prefix anfangen
 * @param {Object} obj
 * @param {string} prefix
 * @returns {Object}
 */
export const getPrefixedMethods = (obj: any, prefix: string): Record<string, Function> => {
  const methods: Record<string, Function> = {};
  getAllPropertyNames(obj)
    .filter((key) => key.startsWith(prefix))
    .forEach(
      (key) => (methods[key.substringAfter(prefix)] = (...args: any[]) => obj[key](...args)),
    );
  return methods;
};

/**
 * Zählt die Anzahl der Parameter, die nicht empty sind
 * @param {...any} parameter
 * @returns {number}
 */
export const countNotEmpty = (...parameter: any[]): number => {
  let count = 0;
  for (let i = 0; i < parameter.length; i++) {
    if (!isEmpty(parameter[i])) {
      count += 1;
    }
  }
  return count;
};

/**
 * vergleicht zwei Objektgraphen und gibt das Delta aus
 * @param {Object} a
 * @param {Object} b
 * @return {{different: Array, missing_from_first: Array, missing_from_second: Array}}
 */
export const deepDiff = (
  a: any,
  b: any,
  equal: (a: any, b: any, key: any) => boolean = (a, b, key) => isEqualEpsilon(a, b),
): { different: string[]; missing_from_first: string[]; missing_from_second: string[] } => {
  const result = {
    different: [] as string[],
    missing_from_first: [] as string[],
    missing_from_second: [] as string[],
  };

  _.reduce(
    a,
    (result, value, key) => {
      if (b != null && b.hasOwnProperty(key)) {
        if (equal(value, b[key], key)) {
          return result;
        } else {
          if (typeof a[key] != typeof {} || typeof b[key] != typeof {}) {
            //dead end.
            result.different.push(key);
            return result;
          } else {
            const deeper = deepDiff(a[key], b[key], equal);
            result.different = result.different.concat(
              _.map(deeper.different, (sub_path) => {
                return key + "." + sub_path;
              }),
            );

            result.missing_from_second = result.missing_from_second.concat(
              _.map(deeper.missing_from_second, (sub_path) => {
                return key + "." + sub_path;
              }),
            );

            result.missing_from_first = result.missing_from_first.concat(
              _.map(deeper.missing_from_first, (sub_path) => {
                return key + "." + sub_path;
              }),
            );
            return result;
          }
        }
      } else {
        result.missing_from_second.push(key);
        return result;
      }
    },
    result,
  );

  _.reduce(
    b,
    (result, value, key) => {
      if (a != null && a.hasOwnProperty(key)) {
        return result;
      } else {
        result.missing_from_first.push(key);
        return result;
      }
    },
    result,
  );

  return result;
};

/**
 * loggt die Differenzen zweier Objekte ausführlich auf die console aus
 * @param {Object} a
 * @param {Object} b
 * @param {function} equal
 */
export const logDeepDiffWithValues = (
  a: any,
  b: any,
  equal: (a: any, b: any, key: any) => boolean = (a, b, key) => isEqualEpsilon(a, b),
): void => {
  const diff = deepDiff(a, b, equal);
  const table = [diff.different, diff.missing_from_second, diff.missing_from_first].flatMap(
    (paths) =>
      paths.flatMap((path) => ({
        path,
        a: _.get(a, path),
        b: _.get(b, path),
      })),
  );
  console.log("logDeepDiffWithValues----");
  console.log("diff", diff);
  console.log("a", a);
  console.log("b", b);
  console.log(table);
  console.table(table);
  console.log("----");
};

/**
 * Prüft ob zwei Objektgraphen einen Unterschied haben (wenn einen das Delta nicht interessiert)
 * @param {Object} a
 * @param {Object} b
 * @param {function} equal
 * @returns {boolean}
 */
export const hasDeepDiff = (a: any, b: any, equal: any): boolean => {
  const diff = deepDiff(a, b, equal);
  return [diff.different, diff.missing_from_first, diff.missing_from_second].some(
    (x) => x.length > 0,
  );
};

/**
 * Löscht item aus array (mutiert array)
 * @param {any[]} array
 * @param {any} item
 * @return {any[]}
 */
export const remove = <T>(array: T[], item: T): T[] => {
  const index = array.indexOf(item);
  if (index >= 0) {
    array.splice(index, 1);
  }
  return array;
};

/**
 * Liefert zu einem Event-Namen einen Methoden-Namen, wie "selectElement" => "onSelectElement"
 * @param {string} event
 * @return {string}
 */
export const getMethodNameForEvent = (event: string): string => {
  return "on" + event.charAt(0).toUpperCase() + event.slice(1);
};

/**
 * pairwise([1]) => []
 * pairwise([1,2,3]) => [[1,2],[2,3]]
 * pairwise([1,2,3,4,5],2,2) => [[1,2],[3,4]]
 * @param {Array} array
 * @param {number} windowSize
 * @param {number} stepSize
 * @returns {Array}
 */
export const pairwise = <T>(array: T[], windowSize: number = 2, stepSize: number = 1): T[][] => {
  if (array == null) {
    return [];
  }
  const pairs: T[][] = [];
  for (let i = 0; i < array.length - windowSize + 1; i += stepSize) {
    pairs.push(array.slice(i, i + windowSize));
  }
  return pairs;
};

/**
 * gibt eine Function zurück, welche für jeden Eintrag aufgerufen wird -
 * bei einem Array/Set für jedes Element, sonst für den übergebenen Wert.
 * @param {any} objectOrArray
 * @returns {function}
 */
export const forOneOrEach = (objectOrArray: any): ((callback: (item: any) => void) => void) => {
  if (Array.isArray(objectOrArray) || objectOrArray instanceof Set) {
    return (callback) => objectOrArray.forEach(callback);
  }
  return (callback) => callback(objectOrArray);
};

/**
 * schläft eine bestimmte Zeit, kann mittels await aufgerufen werden
 * @param {number} ms
 * @returns {Promise<unknown>}
 */
export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * schläft, bis predicate true is
 * @param {function} predicate
 * @returns {Promise<unknown>}
 */
export const sleepUntil = (predicate: () => boolean): Promise<void> => {
  return new Promise((resolve, reject) => delayUntil(resolve, predicate));
};

/**
 * Format bytes as human-readable text.
 * @param {number} bytes Number of bytes.
 * @param {boolean} si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024.
 * @param {number} dp Number of decimal places to display.
 * @return {string} Formatted string.
 */
export const humanFileSize = (bytes: number, si: boolean = false, dp: number = 1): string => {
  const thresh = si ? 1000 : 1024;
  if (Math.abs(bytes) < thresh) {
    return bytes + " B";
  }
  const units = si
    ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
    : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
  let u = -1;
  const r = 10 ** dp;
  do {
    bytes /= thresh;
    ++u;
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);
  return bytes.toFixed(dp) + " " + units[u];
};

/**
 * Escapes a string for use as regular expression
 * @param string
 * @returns {*}
 */
export function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
}

/**
 * gibt das häufigste Element einer Liste zurück (oder eines der häufigsten anhand einer gruppierenden Mapfunktion)
 * @param list
 * @returns {*}
 */
export const maxFrequent = <T>(list: T[], mapper: (x: T) => any = (x) => x): T | undefined =>
  Array.from(groupBy(list, mapper)).reduce(
    (entryMax, entry) =>
      entryMax != null && entryMax[1].length > entry[1].length ? entryMax : entry,
    undefined as [any, T[]] | undefined,
  )?.[1][0];

/**
 * gibt das seltenste Element einer Liste zurück (oder eines der seltensten einer gruppierenden Mapfunktion)
 * @param list
 * @returns {*}
 */
export const minFrequent = <T>(list: T[], mapper: (x: T) => any = (x) => x): T | undefined =>
  Array.from(groupBy(list, mapper)).reduce(
    (entryMax, entry) =>
      entryMax != null && entryMax[1].length < entry[1].length ? entryMax : entry,
    undefined as [any, T[]] | undefined,
  )?.[1][0];

/**
 * gibt den Median einer Liste zurück
 * @param numbers
 * @returns {unknown|number}
 */
export function median(numbers: number[]): number {
  const sorted = Array.from(numbers).sort((a, b) => a - b);
  const length = sorted.length;
  const index = Math.max(0, Math.min(length - 1, Math.round(sorted.length / 2)));
  return sorted[index];
}

/**
 * Gibt alle mehrfach vorkommenden Elemente eines Arrays zurück
 */
export function duplicates<T>(arr: T[]): T[] {
  const countMap = new Map<T, number>();
  for (const value of arr) {
    countMap.set(value, (countMap.get(value) ?? 0) + 1);
  }
  return arr.filter((value) => countMap.get(value)! > 1);
}

/**
 * Finds the longest common initial part in the given arrays and returns it as a new array.
 * Example:
 * a = [0,1,2,3]
 * b = [0,1,4,5]
 * [0,1] == getFirstCommonPart([a, b])
 * @template T
 * @param {T[][]} paths - An array of arrays to compare.
 * @param {(a: T, b: T) => boolean} [equals=(a, b) => a == b] - A function to check if two elements are equal.
 * @returns {T[]} - The longest common initial part found across all arrays.
 */
export function getFirstCommonPart<T>(
  paths: T[][],
  equals: (a: T, b: T) => boolean = (a, b) => a == b,
): T[] {
  const result: T[] = [];
  if (paths.length === 0) return result;
  let i = 0;
  const firstPath = paths[0];
  const minLength = Math.min(...paths.map((p) => p.length));
  outerLoop: for (; i < minLength; i++) {
    const current = firstPath[i];
    for (let j = 1; j < paths.length; j++) {
      const path = paths[j];
      if (i >= path.length || !equals(path[i], current)) {
        break outerLoop;
      }
    }
    result.push(current);
  }
  return result;
}

/**
 * gibt eine um ein Index-Delta rotierte Version des Arrays zurück
 * @template T
 * @param {T[]} arr - An array
 * @param {number} delta
 * @returns {T[]} the rotated array
 */
export function toRotatedArray<T>(arr: T[], delta: number): T[] {
  const n = arr.length;
  if (n === 0) return arr;
  delta = ((delta % n) + n) % n;
  return arr.slice(delta).concat(arr.slice(0, delta));
}
